import type { AppState, FamilyMissionCategory } from './models';

export type FamilyStrengthKey = 'observe' | 'plan' | 'learn' | 'connect' | 'recover';

export interface FamilyStrength {
  key: FamilyStrengthKey;
  label: string;
  shortLabel: string;
  value: number;
  description: string;
}

export interface FamilyMissionRecommendation {
  id: string;
  title: string;
  description: string;
  category: FamilyMissionCategory;
  estimatedMinutes: number;
  rewardXp: number;
  conversationPrompt: string;
}

export interface WeeklyFamilyInsight {
  weekLabel: string;
  headline: string;
  narrative: string;
  highlight: string;
  challenge: string;
  nextStep: string;
  strengths: FamilyStrength[];
  dominantStrength: FamilyStrength;
  growthStrength: FamilyStrength;
  recordCount: number;
  approvedMissionCount: number;
  quizCount: number;
  learningCount: number;
  familySignalCount: number;
  recommendedMission: FamilyMissionRecommendation;
  encouragements: string[];
}

export interface FamilyCollectionItem {
  id: string;
  title: string;
  description: string;
  icon: string;
  unlocked: boolean;
  unlockRule: string;
  earnedAt?: string;
}

const DAY = 86_400_000;

function mondayStart(input: Date): Date {
  const date = new Date(input);
  date.setHours(0, 0, 0, 0);
  const day = date.getDay();
  date.setDate(date.getDate() - (day === 0 ? 6 : day - 1));
  return date;
}

function inRange(value: string | undefined, start: Date, end: Date): boolean {
  if (!value) return false;
  const time = new Date(value).getTime();
  return Number.isFinite(time) && time >= start.getTime() && time < end.getTime();
}

function clamp(value: number): number {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function koDate(date: Date): string {
  return `${date.getMonth() + 1}월 ${date.getDate()}일`;
}

const RECOMMENDATIONS: Record<FamilyStrengthKey, FamilyMissionRecommendation> = {
  observe: {
    id: 'family-observe-price',
    title: '우리 집 가격 탐정',
    description: '가족이 자주 사는 물건 하나를 정하고 두 곳의 가격·용량을 비교해요.',
    category: 'record',
    estimatedMinutes: 10,
    rewardXp: 35,
    conversationPrompt: '가격 외에 함께 비교해야 할 것은 무엇이었나요?',
  },
  plan: {
    id: 'family-plan-weekend',
    title: '주말 예산 세 칸',
    description: '주말에 쓸 돈을 꼭 필요한 것·즐거운 것·남길 것으로 나누어 계획해요.',
    category: 'saving',
    estimatedMinutes: 12,
    rewardXp: 40,
    conversationPrompt: '세 칸 중 가장 정하기 어려웠던 것은 무엇이었나요?',
  },
  learn: {
    id: 'family-learn-news',
    title: '뉴스 한 줄 번역가',
    description: '경제 뉴스 한 줄을 골라 아이의 말로 다시 설명하고 가족의 예를 하나 찾아요.',
    category: 'learning',
    estimatedMinutes: 10,
    rewardXp: 40,
    conversationPrompt: '이 뉴스가 우리 생활과 만나는 지점은 어디였나요?',
  },
  connect: {
    id: 'family-connect-choice',
    title: '우리 가족 선택 회의',
    description: '이번 주 기억에 남는 선택 하나를 말하고 서로의 이유를 판단 없이 들어요.',
    category: 'family',
    estimatedMinutes: 8,
    rewardXp: 45,
    conversationPrompt: '서로의 이유 중 새롭게 알게 된 점은 무엇인가요?',
  },
  recover: {
    id: 'family-recover-retry',
    title: '다시 뭉치는 스노우볼',
    description: '이번 주 아쉬웠던 선택 하나를 고르고 다음에는 어떻게 다시 시도할지 정해요.',
    category: 'habit',
    estimatedMinutes: 8,
    rewardXp: 45,
    conversationPrompt: '다시 시도할 때 가족이 어떤 도움을 주면 좋을까요?',
  },
};

export function buildWeeklyFamilyInsight(state: AppState, referenceDate = new Date()): WeeklyFamilyInsight {
  const start = mondayStart(referenceDate);
  const end = new Date(start.getTime() + 7 * DAY);
  const records = state.records.filter((item) => inRange(item.occurredAt, start, end));
  const missions = state.missions.filter((item) => inRange(item.approvedAt ?? item.submittedAt, start, end));
  const quizzes = state.quizAttempts.filter((item) => inRange(item.completedAt, start, end));
  const learning = state.learningProgress.filter((item) => item.completed && inRange(item.completedAt, start, end));
  const messages = state.messages.filter((item) => inRange(item.createdAt, start, end));
  const familyMissions = state.familyMissionChallenges.filter((item) => inRange(item.reviewedAt ?? item.submittedAt ?? item.updatedAt, start, end));
  const approvedMissionCount = missions.filter((item) => item.status === 'approved').length;
  const familySignalCount = messages.length + familyMissions.filter((item) => ['submitted', 'approved', 'completed'].includes(item.status)).length;
  const retrySignals = state.missions.filter((item) => item.status === 'held').length + state.quizAttempts.filter((item) => item.reviewedWrongAnswers).length;

  const strengths: FamilyStrength[] = [
    { key: 'observe', label: '관찰하는 힘', shortLabel: '관찰', value: clamp(records.length * 24 + quizzes.length * 8), description: '돈의 흐름과 경제 신호를 알아차리는 힘' },
    { key: 'plan', label: '계획하는 힘', shortLabel: '계획', value: clamp(records.filter((item) => item.type === 'saving').length * 28 + approvedMissionCount * 16 + (state.goal.currentAmount > 0 ? 18 : 0)), description: '목표를 작은 행동으로 나누는 힘' },
    { key: 'learn', label: '이해하는 힘', shortLabel: '이해', value: clamp(quizzes.length * 32 + learning.length * 36), description: '경제 개념을 생활의 말로 바꾸는 힘' },
    { key: 'connect', label: '연결하는 힘', shortLabel: '연결', value: clamp(familySignalCount * 32 + state.passportVisits.filter((item) => item.status === 'approved').length * 10), description: '선택을 가족과 현실 경험에 연결하는 힘' },
    { key: 'recover', label: '다시 시작하는 힘', shortLabel: '회복', value: clamp(retrySignals * 28 + Math.min(state.growthSummary.currentStreak, 7) * 8 + 18), description: '실수 뒤에 다시 시도하는 힘' },
  ];

  const dominantStrength = strengths.reduce((best, item) => item.value > best.value ? item : best, strengths[0]);
  const growthStrength = strengths.reduce((lowest, item) => item.value < lowest.value ? item : lowest, strengths[0]);
  const recommendedMission = RECOMMENDATIONS[growthStrength.key];
  const totalSignals = records.length + missions.length + quizzes.length + learning.length + familySignalCount;
  const headline = totalSignals === 0
    ? '이번 주의 첫 눈 한 줌을 기다리고 있어요'
    : `${dominantStrength.shortLabel}의 힘이 가장 선명하게 자랐어요`;
  const narrative = totalSignals === 0
    ? `${state.child.nickname}가 다시 돌아오는 순간부터 이번 주 성장이 시작됩니다. 결과보다 작은 행동 하나를 함께 골라 주세요.`
    : `${state.child.nickname}는 이번 주 돈 기록 ${records.length}회, 미션 ${missions.length}회, 경제 학습 ${quizzes.length + learning.length}회를 경험했어요. 특히 ${dominantStrength.description}이 눈에 띄었습니다.`;
  const highlight = records.length
    ? `기록 속 선택 이유가 ${records.length}번 남아 있어 대화를 시작할 단서가 충분해요.`
    : '아직 기록이 적으므로 금액보다 오늘 기억나는 선택 하나를 먼저 물어보는 것이 좋아요.';
  const challenge = `${growthStrength.label}은 ${growthStrength.value}%로, 이번 주에는 한 번의 짧은 가족 활동이 가장 큰 도움이 됩니다.`;
  const nextStep = `${recommendedMission.title}을 약 ${recommendedMission.estimatedMinutes}분 동안 함께하면 다음 주 추천이 달라집니다.`;
  const encouragements = [
    `${state.child.nickname}아, 결과보다 네가 이유를 생각한 과정이 정말 좋았어.`,
    `이번 주에도 다시 돌아와 기록한 힘이 멋져!`,
    `${dominantStrength.shortLabel}의 힘이 자라는 게 보여. 다음 선택도 믿고 응원할게.`,
    `잘한 선택뿐 아니라 다시 해본 선택도 우리 가족의 소중한 기록이야.`,
  ];

  return {
    weekLabel: `${koDate(start)}–${koDate(new Date(end.getTime() - DAY))}`,
    headline,
    narrative,
    highlight,
    challenge,
    nextStep,
    strengths,
    dominantStrength,
    growthStrength,
    recordCount: records.length,
    approvedMissionCount,
    quizCount: quizzes.length,
    learningCount: learning.length,
    familySignalCount,
    recommendedMission,
    encouragements,
  };
}

export function buildFamilyCollection(state: AppState): FamilyCollectionItem[] {
  const approvedFamilyMissions = state.familyMissionChallenges.filter((item) => ['approved', 'completed'].includes(item.status));
  const marketVisit = state.passportVisits.find((item) => item.status === 'approved' && item.placeId.includes('market'));
  const firstMessage = state.messages[0];
  const activeWeeks = Math.max(0, Math.floor(state.growthSummary.completedBoardDays / 5));
  const collection: FamilyCollectionItem[] = [
    { id: 'family-first-cheer', title: '첫 응원 눈꽃', description: '가족의 첫 응원을 아이의 냥장고에 보관했어요.', icon: '💌', unlocked: state.messages.length >= 1, unlockRule: '응원 메시지 1회', earnedAt: firstMessage?.createdAt },
    { id: 'family-choice-talk', title: '선택 대화 리본', description: '결과보다 선택의 이유를 함께 들었어요.', icon: '🎗️', unlocked: state.messages.length >= 3, unlockRule: '가족 응원·대화 3회', earnedAt: state.messages[2]?.createdAt },
    { id: 'family-mission-star', title: '공동 미션 별', description: '아이와 부모가 하나의 가족 미션을 완주했어요.', icon: '⭐', unlocked: approvedFamilyMissions.length >= 1, unlockRule: '가족 미션 승인 1회', earnedAt: approvedFamilyMissions[0]?.reviewedAt },
    { id: 'family-market-map', title: '우리 가족 가격 지도', description: '현실 공간에서 가격과 선택을 함께 관찰했어요.', icon: '🗺️', unlocked: Boolean(marketVisit), unlockRule: '시장 패스포트 승인', earnedAt: marketVisit?.reviewedAt },
    { id: 'family-week-snowflake', title: '한 주 눈꽃', description: '가족이 한 주의 선택을 함께 돌아봤어요.', icon: '❄️', unlocked: activeWeeks >= 1, unlockRule: '성장 활동 5일', earnedAt: state.growthSummary.lastActivityAt },
  ];
  const completedCore = collection.filter((item) => item.id !== 'family-crew-emblem').every((item) => item.unlocked);
  collection.push({ id: 'family-crew-emblem', title: '가족 탐험대 엠블럼', description: '관찰·대화·미션·현실 탐험을 가족의 성장 이야기로 완성했어요.', icon: '🏔️', unlocked: completedCore, unlockRule: '가족 컬렉션 5종 완성', earnedAt: completedCore ? state.growthSummary.lastActivityAt : undefined });
  return collection;
}
